import { sendFeedbackFetch } from 'api/feedback';
import Button from 'common/Button/Button';
import Input from 'common/Input/Input';
import ReactInputMask from 'react-input-mask';
import { useId, useState } from 'react';
import './Feedback.css';
import { useLanguage } from 'hooks/useLanguage';
import { useContent } from 'hooks/useContent';

/**
 * Форма зворотного зв'язку — ADR-0008.
 *
 * Прибрано побуквенну анімацію підписів через innerHTML і плаваючий підпис.
 * Додано стани: надсилання, помилка, успіх. Поля (name, phone, message)
 * ідуть на бекенд як є — це він формує текст і шле в Telegram, а не фронтенд:
 * токен бота лишається в backend/.env, на клієнт не потрапляє.
 *
 * Телефон і пошта в .feedback-direct — з CMS (api::contact-info, ADR-0017):
 * перший номер зі списку, бо тут це «зателефонуйте нам», а не повний перелік.
 */
export default function Feedback({ standalone = false }) {
  const language = useLanguage();
  const [status, setStatus] = useState('idle');
  const phoneId = useId();
  const { data: contactInfo } = useContent('contactInfo');
  const directPhone = contactInfo?.phones?.[0];
  // На окремій сторінці /feedback цей заголовок є головним заголовком документа,
  // на головній — заголовком секції.
  const Title = standalone ? 'h1' : 'h2';

  async function handleSubmit(event) {
    event.preventDefault();
    if (status === 'sending') return;

    const form = event.target;
    setStatus('sending');

    const [error] = await sendFeedbackFetch({
      name: form.name.value,
      phone: form.phone.value,
      message: form.message.value,
    });

    if (error) {
      setStatus('error');
      return;
    }

    form.reset();
    setStatus('sent');
  }

  const submitLabel = {
    idle: language === 'en' ? 'Send' : 'Відіслати',
    sending: language === 'en' ? 'Sending' : 'Надсилаємо',
    sent: language === 'en' ? 'Sent' : 'Надіслано',
    error: language === 'en' ? 'Send again' : 'Спробувати ще раз',
  }[status];

  return (
    <section className={`feedback section ${standalone ? '' : 'section--sunken'}`} id='feedback'>
      <div className='container feedback-wrapper'>
        <div className='feedback-intro'>
          <Title className={standalone ? 'page-title' : 'section-title'}>
            {language === 'en' ? 'Feedback' : "Зворотній зв'язок"}
          </Title>
          <p className='feedback-note'>
            {language === 'en'
              ? 'Write to us about membership, ranks or the register. We will answer by phone.'
              : 'Напишіть нам про членство, розряди або реєстр. Ми відповімо за вказаним телефоном.'}
          </p>
          <div className='feedback-direct'>
            {directPhone && (
              <a href={`tel:${directPhone.number.replace(/[^\d+]/g, '')}`} className='tnum'>
                {directPhone.number}
              </a>
            )}
            {contactInfo?.email && <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>}
          </div>
        </div>

        <form onSubmit={handleSubmit} className='feedback-form' noValidate={false}>
          <Input labelText={language === 'en' ? 'Name' : "Ім'я"} name='name' required autoComplete='name' />

          <div className='form-control'>
            <label className='label' htmlFor={phoneId}>
              {language === 'en' ? 'Phone number' : 'Номер телефону'}
            </label>
            <ReactInputMask
              className='input'
              id={phoneId}
              mask='+38 (099) 999-99-99'
              name='phone'
              autoComplete='tel'
              required
            />
          </div>

          <Input
            labelText={language === 'en' ? 'Your question' : 'Ваше питання'}
            name='message'
            textarea
            required
          />

          <div className='feedback-submit'>
            <Button
              variant='primary'
              name='button'
              buttonText={submitLabel}
              type='submit'
              disabled={status === 'sending'}
            />
            {status === 'sent' && (
              <p className='form-status form-status--ok' role='status'>
                {language === 'en' ? 'Thank you, we received your message.' : 'Дякуємо, ми отримали ваше повідомлення.'}
              </p>
            )}
            {status === 'error' && (
              <p className='form-status form-status--error' role='alert'>
                {language === 'en'
                  ? 'The message was not sent. Please try again or call us.'
                  : 'Повідомлення не надіслано. Спробуйте ще раз або зателефонуйте нам.'}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
